<template>
  <div class="px-0">
    <div class="signature-wrap" :class="{ 'is-disabled': disabled }">
      <canvas
        ref="canvas"
        @pointerdown="startStroke"
        @pointermove="drawStroke"
        @pointerup="endStroke"
        @pointerleave="endStroke"
      ></canvas>
      <span class="signature-hint" v-if="!value">{{ $t("dvir.signHere") }}</span>
    </div>
    <b-button
      v-if="!hideClear"
      class="w-fit-content h-40px mt-3 f-14 d-flex justify-content-center align-items-center"
      variant="outline-primary"
      :disabled="disabled || !value"
      @click="clear"
    >
      {{ $t("dvir.clearSignature") }}
    </b-button>
  </div>
</template>
<script>
const HEIGHT = 150;
export default {
  name: "SignaturePad",
  props: {
    // A base64 PNG data URL, or "" when nothing has been drawn.
    value: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Let the parent lay the Clear button out itself (alongside its own Ok).
    hideClear: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      drawing: false,
      // Set while emitting our own stroke, so the watcher below doesn't redraw
      // the image we just drew.
      emitting: false,
    };
  },
  watch: {
    value() {
      if (this.emitting) {
        this.emitting = false;
        return;
      }
      // The value can land after mount (an existing signature being fetched),
      // so paint it onto the canvas when it changes from the outside.
      this.setupCanvas();
    },
  },
  methods: {
    setupCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      // Back the canvas with device pixels so the line isn't blurry on retina.
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = HEIGHT * ratio;
      canvas.style.height = `${HEIGHT}px`;
      const context = canvas.getContext("2d");
      context.scale(ratio, ratio);
      context.lineWidth = 2;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = "#212529";
      // Resizing wipes the canvas, so put any existing signature back.
      if (this.value) {
        const image = new Image();
        image.onload = () => context.drawImage(image, 0, 0, canvas.offsetWidth, HEIGHT);
        image.src = this.value;
      }
    },
    pointFrom(event) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    },
    startStroke(event) {
      if (this.disabled) return;
      this.drawing = true;
      const context = this.$refs.canvas.getContext("2d");
      const point = this.pointFrom(event);
      context.beginPath();
      context.moveTo(point.x, point.y);
      // Keep drawing even if the pointer leaves the canvas mid-stroke.
      if (this.$refs.canvas.setPointerCapture) {
        this.$refs.canvas.setPointerCapture(event.pointerId);
      }
    },
    drawStroke(event) {
      if (!this.drawing) return;
      event.preventDefault();
      const context = this.$refs.canvas.getContext("2d");
      const point = this.pointFrom(event);
      context.lineTo(point.x, point.y);
      context.stroke();
    },
    endStroke() {
      if (!this.drawing) return;
      this.drawing = false;
      this.emitting = true;
      this.$emit("input", this.$refs.canvas.toDataURL("image/png"));
    },
    clear() {
      const canvas = this.$refs.canvas;
      canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
      this.emitting = true;
      this.$emit("input", "");
    },
  },
  mounted() {
    this.setupCanvas();
    window.addEventListener("resize", this.setupCanvas);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setupCanvas);
  },
};
</script>
<style scoped>
.signature-wrap {
  position: relative;
  border: 1px dashed #e3e3e3;
  border-radius: 4px;
  background-color: #ffffff;
}
.signature-wrap canvas {
  display: block;
  width: 100%;
  touch-action: none;
  cursor: crosshair;
}
.signature-wrap.is-disabled {
  background-color: #fafafa;
}
.signature-wrap.is-disabled canvas {
  cursor: not-allowed;
}
.signature-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #a9a9a9;
  font-size: 12px;
  pointer-events: none;
}
</style>
